import React, { useCallback, useMemo, useRef, useState } from 'react';

import { ListCardGroup } from 'components/list-card-group';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';

import { useProductWizardContext } from '../../product-wizard';
import { PickerAspectsUpdateDialog } from '../product-detail-flow/dialogs/picker-aspects-update-dialog';
import { SelectorContainer } from '../styled';

import { PickerAspectOption } from './picker-aspect-option';
import { PickerAspectOptionData, getPickerAspects } from './utils';

export interface PickerAspectSelectorProps {
  pickerAspectId: string;
  isFirstAspect: boolean;
}

export const PickerAspectSelector = ({
  pickerAspectId,
  isFirstAspect,
}: PickerAspectSelectorProps) => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const {
    pickerSelections,
    selectPickerAspectOption,
    isAnySlotCustomized,
  } = useProductWizardContext();
  const [showDialog, setShowDialog] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);

  const { pickerAspect, pickerAspectOptions } = useMemo(
    () => getPickerAspects(indexedMenu, posServiceMode, pickerAspectId),
    [indexedMenu, posServiceMode, pickerAspectId]
  );

  const onConfirm = useCallback(() => {
    pendingAction.current?.();
    setShowDialog(false);
  }, []);

  const onCancel = useCallback(() => {
    pendingAction.current = null;
    setShowDialog(false);
  }, []);

  const onSelected = useCallback(
    (pickerAspectId: string, aspectOptionId: string) => {
      if (isAnySlotCustomized()) {
        // save the action to be executed after the dialog is closed
        pendingAction.current = () => selectPickerAspectOption(pickerAspectId, aspectOptionId);
        setShowDialog(true);
        return;
      }
      selectPickerAspectOption(pickerAspectId, aspectOptionId);
    },
    [selectPickerAspectOption, isAnySlotCustomized]
  );

  return (
    <SelectorContainer>
      <ListCardGroup
        header={pickerAspect.name?.locale ?? ''}
        items={pickerAspectOptions}
        renderItem={({
          item: aspectOptionData,
          index,
        }: {
          item: PickerAspectOptionData;
          index: number;
        }) => (
          <PickerAspectOption
            key={index}
            aspectOption={aspectOptionData.aspectOption}
            product={aspectOptionData.defaultProduct}
            selected={aspectOptionData.aspectOption.id === pickerSelections[pickerAspect.id]}
            onSelected={() => onSelected(pickerAspectId, aspectOptionData.aspectOption.id)}
            showPriceUpcharge={!isFirstAspect}
            upcharge={aspectOptionData.upcharge}
          />
        )}
      />
      <PickerAspectsUpdateDialog
        showDialog={showDialog}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </SelectorContainer>
  );
};
